import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import TopHeader from "@/components/TopHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  Star, Calendar, Clock, Video, 
  Heart, Brain, UserCheck, CreditCard, Loader2, ChevronRight, IndianRupee, AlertTriangle, Shield, RefreshCw
} from "lucide-react";
import { format } from "date-fns";

// Helper to resolve image URLs (handles both external URLs and uploaded files)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api')) {
    return `${API.replace('/api', '')}${url}`;
  }
  return url;
};

const SPECIALTIES = [
  { id: 'maternal_mental_health', label: 'Mental Health Therapist', icon: Brain },
  { id: 'life_coaching', label: 'Life Coach', icon: Heart },
  { id: 'postpartum_support', label: 'Postpartum Specialist', icon: UserCheck },
];

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const Experts = () => {
  const { user } = useAuth();
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [specialty, setSpecialty] = useState(null);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [expertDetailsOpen, setExpertDetailsOpen] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingConfirmOpen, setBookingConfirmOpen] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    fetchExperts();
    loadRazorpayScript();
  }, [specialty]);

  const fetchExperts = async () => {
    setLoading(true);
    try {
      const params = specialty ? `?specialty=${specialty}` : '';
      const response = await axios.get(`${API}/experts${params}`, { withCredentials: true });
      setExperts(response.data);
    } catch (error) {
      console.error("Fetch experts error:", error);
      toast.error("Failed to load experts");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async (expertId) => {
    setSlotsLoading(true);
    try {
      const response = await axios.get(`${API}/experts/${expertId}/slots`, { withCredentials: true });
      setAvailableSlots(response.data);
    } catch (error) {
      console.error("Fetch slots error:", error);
      toast.error("Failed to load available slots");
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const openExpertDetails = (expert) => {
    setSelectedExpert(expert);
    setExpertDetailsOpen(true);
    fetchAvailableSlots(expert.expert_id);
  };

  const closeExpertDetails = () => {
    setExpertDetailsOpen(false);
    setSelectedExpert(null);
    setAvailableSlots([]);
    setSelectedSlot(null);
  };

  const selectSlot = (slot) => {
    setSelectedSlot(slot);
    setBookingConfirmOpen(true);
  };

  const initiateBooking = useCallback(async () => {
    if (!selectedSlot || !selectedExpert) return;
    
    setPaymentLoading(true);
    
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      toast.error("Failed to load payment gateway. Please refresh and try again.");
      setPaymentLoading(false);
      return;
    }
    
    try {
      // Create booking and get payment order
      const response = await axios.post(
        `${API}/experts/${selectedExpert.expert_id}/slots/${selectedSlot.slot_id}/book`,
        {},
        { withCredentials: true }
      );

      const options = {
        key: response.data.key_id,
        amount: response.data.amount,
        currency: response.data.currency,
        name: "WOMBS",
        description: `Session with ${selectedExpert.name}`,
        order_id: response.data.order_id,
        handler: async (paymentResponse) => {
          try {
            // Verify payment
            await axios.post(`${API}/bookings/verify-payment`, {
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature,
              booking_id: response.data.booking_id
            }, { withCredentials: true });
            
            toast.success("Payment successful! Session booked. Check your email for confirmation.");
            setBookingConfirmOpen(false);
            closeExpertDetails();
            fetchExperts();
          } catch (error) {
            toast.error("Payment verification failed. Please contact support.");
          }
          setPaymentLoading(false);
        },
        prefill: {
          name: user?.name,
          email: user?.email,
        },
        theme: {
          color: "#7c9082"
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled");
            setPaymentLoading(false);
          }
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
      
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate booking");
      setPaymentLoading(false);
    }
  }, [selectedSlot, selectedExpert, user]);

  const getSpecialtyIcon = (specialtyId) => {
    const found = SPECIALTIES.find(s => s.id === specialtyId);
    return found ? found.icon : Brain;
  };

  const getSpecialtyLabel = (specialtyId) => {
    const found = SPECIALTIES.find(s => s.id === specialtyId);
    return found ? found.label : specialtyId;
  };

  // Group slots by date
  const groupSlotsByDate = (slots) => {
    const groups = {};
    slots.forEach(slot => {
      const date = format(new Date(slot.start_time), 'yyyy-MM-dd');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(slot);
    });
    return groups;
  };

  return (
    <div className="min-h-screen bg-cream pb-24">
      <Navigation />
      <TopHeader title="Expert Sessions" />

      {/* Specialty Filters */}
      <div className="px-6 py-4 border-b border-sage/10 bg-white/30">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-2">
          <Button 
            variant={specialty === null ? "default" : "outline"}
            size="sm"
            onClick={() => setSpecialty(null)}
            className={`rounded-full ${specialty === null ? 'bg-sage text-white' : 'border-sage/30 text-sage-darker'}`}
          >
            All Experts
          </Button>
          {SPECIALTIES.map((spec) => {
            const Icon = spec.icon;
            return (
              <Button 
                key={spec.id}
                variant={specialty === spec.id ? "default" : "outline"}
                size="sm"
                onClick={() => setSpecialty(spec.id)}
                className={`rounded-full ${specialty === spec.id ? 'bg-sage text-white' : 'border-sage/30 text-sage-darker'}`}
              >
                <Icon className="w-4 h-4 mr-1" />
                {spec.label}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Experts Grid */}
      <main className="px-6 py-6" data-testid="experts-main">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="bg-white border-sage/20 animate-pulse">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-full bg-sage/10" />
                      <div className="flex-1 space-y-2">
                        <div className="h-6 bg-sage/10 rounded w-3/4" />
                        <div className="h-4 bg-sage/10 rounded w-1/2" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : experts.length === 0 ? (
            <div className="text-center py-16">
              <UserCheck className="w-16 h-16 text-sage/40 mx-auto mb-4" />
              <p className="text-muted-foreground">No experts found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {experts.map((expert) => {
                const SpecialtyIcon = getSpecialtyIcon(expert.specialty);
                return (
                  <Card 
                    key={expert.expert_id} 
                    className="bg-white border-sage/20 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => openExpertDetails(expert)}
                    data-testid={`expert-${expert.expert_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Avatar className="w-20 h-20 border-2 border-sage/30">
                          <AvatarImage src={resolveImageUrl(expert.picture)} alt={expert.name} />
                          <AvatarFallback className="bg-sage/20 text-sage-dark text-xl">
                            {expert.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h3 className="text-sage-darker font-medium text-lg">{expert.name}</h3>
                          <p className="text-sage-dark text-sm">{expert.title}</p>
                          <div className="flex items-center gap-1 mt-1 text-amber-500">
                            <Star className="w-4 h-4 fill-current" />
                            <span className="text-sm">{expert.rating?.toFixed(1) || '5.0'}</span>
                            <span className="text-muted-foreground text-xs">({expert.total_sessions || 0} sessions)</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Badge variant="outline" className="border-sage/30 text-sage-dark bg-sage/5">
                          <SpecialtyIcon className="w-3 h-3 mr-1" />
                          {getSpecialtyLabel(expert.specialty)}
                        </Badge>
                      </div>
                      
                      <p className="mt-4 text-muted-foreground text-sm line-clamp-2">{expert.bio}</p>
                      
                      <div className="mt-4 flex items-center justify-between">
                        <div className="flex items-center text-sage-dark font-medium">
                          <IndianRupee className="w-4 h-4" />
                          <span>{(expert.hourly_rate || 2000).toLocaleString('en-IN')}</span>
                          <span className="text-muted-foreground text-sm font-normal">/hour</span>
                        </div>
                        <Button 
                          size="sm" 
                          className="bg-sage text-white hover:bg-sage-dark rounded-full"
                          onClick={(e) => { e.stopPropagation(); openExpertDetails(expert); }}
                        >
                          Book Session
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Expert Details & Slots Dialog */}
      <Dialog open={expertDetailsOpen} onOpenChange={(open) => !open && closeExpertDetails()}>
        <DialogContent className="bg-white border-sage/20 max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedExpert && (
            <>
              <DialogHeader>
                <DialogTitle className="text-sage-darker font-serif">
                  Book a Session with {selectedExpert.name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Expert Info */}
                <div className="flex items-center gap-4 p-4 bg-sage/5 rounded-xl">
                  <Avatar className="w-16 h-16 border-2 border-sage/30">
                    <AvatarImage src={selectedExpert.picture} />
                    <AvatarFallback className="bg-sage/20 text-sage-dark">
                      {selectedExpert.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-sage-darker font-medium">{selectedExpert.name}</h3>
                    <p className="text-sage-dark text-sm">{selectedExpert.title}</p>
                    <div className="flex items-center gap-1 mt-1 text-amber-500">
                      <Star className="w-4 h-4 fill-current" />
                      <span className="text-sm">{selectedExpert.rating?.toFixed(1) || '5.0'}</span>
                    </div>
                  </div>
                </div>

                {/* Available Slots */}
                <div>
                  <h4 className="text-sage-darker font-medium mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Available Time Slots
                  </h4>
                  
                  {slotsLoading ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-sage" />
                      <p className="text-muted-foreground mt-2">Loading available slots...</p>
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <div className="text-center py-8 bg-cream rounded-xl">
                      <Calendar className="w-12 h-12 text-sage/40 mx-auto mb-2" />
                      <p className="text-muted-foreground">No available slots at the moment</p>
                      <p className="text-sm text-muted-foreground mt-1">Please check back later</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(groupSlotsByDate(availableSlots)).map(([date, slots]) => (
                        <div key={date} className="border border-sage/20 rounded-xl overflow-hidden">
                          <div className="bg-sage/10 px-4 py-2">
                            <p className="text-sage-darker font-medium">
                              {format(new Date(date), 'EEEE, MMMM d, yyyy')}
                            </p>
                          </div>
                          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {slots.map((slot) => (
                              <button
                                key={slot.slot_id}
                                onClick={() => selectSlot(slot)}
                                className={`p-3 rounded-xl border text-left transition-all ${
                                  selectedSlot?.slot_id === slot.slot_id 
                                    ? 'border-sage bg-sage/10' 
                                    : 'border-sage/20 hover:border-sage hover:bg-sage/5'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4 text-sage-dark" />
                                    <span className="text-sage-darker font-medium">
                                      {format(new Date(slot.start_time), 'h:mm a')}
                                    </span>
                                  </div>
                                  <Badge variant="outline" className="border-sage/30 text-sage-dark">
                                    {slot.duration_minutes} min
                                  </Badge>
                                </div>
                                <div className="mt-2 flex items-center justify-between">
                                  <span className="text-muted-foreground text-sm">
                                    to {format(new Date(slot.end_time), 'h:mm a')}
                                  </span>
                                  <span className="text-sage-dark font-medium">
                                    ₹{(slot.price_inr || 0).toLocaleString('en-IN')}
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Booking Confirmation Dialog */}
      <Dialog open={bookingConfirmOpen} onOpenChange={setBookingConfirmOpen}>
        <DialogContent className="bg-white border-sage/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sage-darker font-serif">Confirm Your Booking</DialogTitle>
          </DialogHeader>
          
          {selectedSlot && selectedExpert && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-sage/5 rounded-xl space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expert</span>
                  <span className="text-sage-darker font-medium">{selectedExpert.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span className="text-sage-darker font-medium">
                    {format(new Date(selectedSlot.start_time), 'MMM d, yyyy')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Time</span>
                  <span className="text-sage-darker font-medium">
                    {format(new Date(selectedSlot.start_time), 'h:mm a')} - {format(new Date(selectedSlot.end_time), 'h:mm a')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="text-sage-darker font-medium">{selectedSlot.duration_minutes} minutes</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Session Type</span>
                  <span className="text-sage-darker font-medium flex items-center gap-1">
                    <Video className="w-4 h-4" /> Video Call
                  </span>
                </div>
              </div>
              
              <div className="p-4 bg-sage/10 rounded-xl">
                <div className="flex justify-between items-center">
                  <span className="text-sage-darker font-medium">Total Amount</span>
                  <span className="text-2xl font-serif text-sage-darker">
                    ₹{(selectedSlot.price_inr || 0).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              {/* Non-refundable & Reschedule Policy Notice */}
              <div className="space-y-2">
                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-amber-700">
                      <p className="font-semibold mb-1">Non-Refundable Payment</p>
                      <p>All payments are final and cannot be refunded once processed.</p>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-start gap-2">
                    <RefreshCw className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-blue-700">
                      <p className="font-semibold mb-1">Rescheduling Policy</p>
                      <p>Sessions can be rescheduled up to 24 hours before the scheduled time. Contact support to reschedule.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <DialogFooter className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setBookingConfirmOpen(false)}
                  className="flex-1 border-sage/30"
                >
                  Cancel
                </Button>
                <Button
                  onClick={initiateBooking}
                  disabled={paymentLoading}
                  className="flex-1 bg-sage text-white hover:bg-sage-dark"
                >
                  {paymentLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Pay & Book
                    </>
                  )}
                </Button>
              </DialogFooter>

              {/* Legal Footer */}
              <div className="pt-2 border-t border-sage/10">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Shield className="w-3 h-3" />
                  <span>By booking, you agree to our <a href="/terms" className="text-sage-dark underline">Terms of Service</a> and <a href="/privacy" className="text-sage-dark underline">Privacy Policy</a>. Payments are securely processed via Razorpay.</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Navigation />
    </div>
  );
};

export default Experts;
